import { WorkflowExecutionType } from "api/api.typing";
import Badge from "components/Badge";
import EmptyState from "components/EmptyState";
import Button from "components/buttons/Button";
import { Tabs, TabsContent, TabsTrigger } from "components/ui/tabs";
import { FileBoxIcon, InfoIcon, MessageSquareMoreIcon } from "lucide-react";
import { SideBar, SideBarPresence } from "pages/project/common/SideBar";
import { SideBarEntityTitle } from "pages/project/common/SideBarEntityTitle";
import { SideBarTabsList } from "pages/project/common/SideBarTabsList";
import { sideBarTabsTriggerClassName } from "pages/project/common/masterDetailLayout";
import { XTM_CAT_TOOL_ID, getCatToolName } from "pages/project/common/taskBase.typing";
import { XtmJobLinksTable } from "pages/project/common/taskBase/list/XtmJobLinksTable";
import { TaskBaseDetail } from "pages/project/common/taskBase/sidebar/TaskBaseDetail";
import TaskBaseFilesDownload from "pages/project/common/taskBase/sidebar/TaskBaseFilesDownload";
import TaskInstructions from "pages/project/common/taskBase/sidebar/TaskInstructions";
import { InstructionsTabWarning } from "pages/project/common/taskBase/sidebar/instructions/InstructionsTabWarning";
import { useHasMandatoryInstructions } from "pages/project/common/taskBase/sidebar/instructions/useHasMandatoryInstructions";
import OfferStatusBadge from "pages/project/offer/components/sidebar/OfferStatusBadge";
import { OfferListType, OfferStatus } from "pages/project/offer/offer.typing";
import { UserHint } from "pages/project/task/components/sidebar/UserHint";
import { useOffer } from "query/offer.query";
import { ReactElement, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useOfferFileStore } from "store/useOfferFileStore";
import { useShallow } from "zustand/react/shallow";

interface Props {
  selectedOfferId?: string;
  onClose: () => void;
  onErrorNode: () => ReactElement;
  onDecline: () => void;
  onAccept: () => void;
  filterType: OfferListType;
}

function OfferSideBar({ selectedOfferId, ...props }: Props) {
  const { t } = useTranslation();
  const { isLoading, selectedOffer } = useOffer(selectedOfferId);
  const [activeTab, setActiveTab] = useState("overview");
  const { hasMandatoryInstructions } = useHasMandatoryInstructions(selectedOffer?.taskId ?? "");

  useEffect(() => {
    setActiveTab("overview");
  }, [selectedOffer?.id]);

  const state = useOfferFileStore(
    useShallow((state) => ({
      shouldDisableAction: state.shouldDisableAction,
      requestZipFullDownload: state.requestZipFullDownload,
    }))
  );

  if (!isLoading && selectedOfferId && !selectedOffer) {
    // todo remove this when get offer by id is ready
    toast.error(
      <div className="ml-2 flex flex-col space-y-1">
        <span className="text-color-primary">{t("offer.detail.error.title")}</span>
        <span className="text-color-secondary text-sm">{t("offer.detail.error.description")}</span>
      </div>,
      {
        id: "no-offer-id",
        className: "bg-color-primary",
      }
    );
    return props.onErrorNode();
  }

  const shouldDisableDownload = selectedOffer ? state.shouldDisableAction(selectedOffer.id) : false;
  const isOfferAccepted =
    !!selectedOffer && [OfferStatus.Accepted, OfferStatus.AcceptedOnBehalf].includes(selectedOffer.status);
  const isXtm =
    !!selectedOffer &&
    selectedOffer.catToolId === XTM_CAT_TOOL_ID &&
    selectedOffer.workflowExecutionType == WorkflowExecutionType.CAT;

  return (
    <SideBarPresence present={!isLoading && !!selectedOffer}>
      {selectedOffer ? (
        <SideBar
          key="offer-detail-panel"
          contentKey={selectedOffer.id}
          onClose={props.onClose}
          title={
            <SideBarEntityTitle code={selectedOffer.taskCode}>
              <Badge color="goldfish">{t("offer.fields.offer")}</Badge>
              <Badge>{selectedOffer.taskType}</Badge>
              {getCatToolName(selectedOffer.catToolId) && (
                <Badge dataTestId="task-type-value">{getCatToolName(selectedOffer.catToolId)}</Badge>
              )}
              <OfferStatusBadge status={selectedOffer.status} />
            </SideBarEntityTitle>
          }
        >
          {!isOfferAccepted && (
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Button color="ternary" label={t("offer.detail.decline")} onClick={props.onDecline} />
              <Button color="primary" label={t("offer.detail.accept")} onClick={props.onAccept} />
            </div>
          )}

          <UserHint fileTabName={isXtm ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")} />

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            // Controlled tabs: value must not follow Dialog RestoreFocus onto a TabsTrigger.
            activationMode="manual"
            className="-mx-6 mt-4 min-w-0 border-t border-gray-300 dark:border-t-gray-600"
          >
            <SideBarTabsList>
              <TabsTrigger value="overview" data-test="overview-tab" className={sideBarTabsTriggerClassName}>
                <InfoIcon className="mr-2 size-4 shrink-0" />
                {t("translation:task.tabs.general")}
              </TabsTrigger>
              <TabsTrigger value="files" data-test="files-tab" className={sideBarTabsTriggerClassName}>
                <FileBoxIcon className="mr-2 size-4 shrink-0" />
                {isXtm ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")}
              </TabsTrigger>
              <TabsTrigger value="instructions" data-test="instructions-tab" className={sideBarTabsTriggerClassName}>
                <MessageSquareMoreIcon className="mr-2 size-4 shrink-0" /> {t("translation:task.tabs.instructions")}
                <InstructionsTabWarning show={hasMandatoryInstructions} />
              </TabsTrigger>
            </SideBarTabsList>

            <TabsContent value="overview" className="px-6">
              <TaskBaseDetail
                selectedTaskBase={selectedOffer}
                taskBaseStartDateLabel={t("task-base.detail.estimatedStartDate")}
                isUserSupplier={true}
                isCanEditSupplierContact={false}
              />
            </TabsContent>

            <TabsContent value="files" className="px-6">
              <div className="my-6">
                <TaskBaseFilesDownload
                  selectedTaskBase={selectedOffer}
                  disableDownload={shouldDisableDownload}
                  onRequestZipFullDownload={(offer, currentLanguage, hubConnectionId) =>
                    state.requestZipFullDownload(offer.id, offer, currentLanguage, hubConnectionId)
                  }
                />
              </div>
              {isXtm ? (
                <div className="my-4 border-t border-gray-200 px-2 pt-4 dark:border-slate-600">
                  <XtmJobLinksTable isTaskOngoing={false} taskId={selectedOffer.taskId} />
                </div>
              ) : (
                <EmptyState
                  title={t("task.detail.delivery-files.title-no-files")}
                  subtitle={t("task.detail.delivery-files.title-no-files-detail")}
                />
              )}
            </TabsContent>

            <TabsContent value="instructions" className="px-6">
              <TaskInstructions taskId={selectedOffer.taskId} />
            </TabsContent>
          </Tabs>
        </SideBar>
      ) : null}
    </SideBarPresence>
  );
}

export default OfferSideBar;
